
import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "@/hooks/use-toast";

interface FeedbackDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const FeedbackDialog: React.FC<FeedbackDialogProps> = ({ open, onOpenChange }) => {
  const { user } = useAuth();
  const [feedbackText, setFeedbackText] = useState("");
  const [feedbackType, setFeedbackType] = useState("suggestion");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!feedbackText.trim()) {
      toast({
        title: "Error",
        description: "Please provide feedback before submitting",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      // Create email content
      const emailContent = `
New ${feedbackType.charAt(0).toUpperCase() + feedbackType.slice(1)} Received:

From: ${user?.email || "Anonymous"}
Type: ${feedbackType}

Feedback:
${feedbackText}

Submitted at: ${new Date().toLocaleString()}
      `;

      // In a real application, you would send this to your backend
      // For now, we'll simulate the email sending
      console.log("Email content to send to teenvisor@gmail.com:", emailContent);

      // Check if the feedback table exists and try to store it as well
      const { error: checkError } = await supabase
        .from('user_feedback')
        .select('id')
        .limit(1);
      
      // If table doesn't exist, just simulate success
      if (checkError) {
        console.log("Feedback table doesn't exist, only sending email");
        // Simulate API call success
        await new Promise(resolve => setTimeout(resolve, 1000));
      } else {
        // Table exists, insert the feedback
        const { error } = await supabase
          .from('user_feedback')
          .insert([
            { 
              user_id: user?.id,
              email: user?.email,
              feedback_text: feedbackText,
              feedback_type: feedbackType,
              created_at: new Date().toISOString(),
            }
          ]);
        
        if (error) {
          console.log("Database insert failed, but email will still be sent:", error);
        }
      }
      
      toast({
        title: "Thanks for your feedback!",
        description: "We've received your feedback and will review it soon.",
      });
      
      setFeedbackText("");
      setFeedbackType("suggestion");
      onOpenChange(false);
    } catch (error) {
      console.error("Error submitting feedback:", error);
      toast({
        title: "Submission failed",
        description: "There was an error submitting your feedback. Please try again later.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold">Share Your Feedback</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4 mt-2">
          <div className="space-y-3">
            <Label>What kind of feedback do you have?</Label>
            <RadioGroup 
              defaultValue="suggestion" 
              value={feedbackType} 
              onValueChange={setFeedbackType}
              className="flex flex-col space-y-2"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="suggestion" id="suggestion" />
                <Label htmlFor="suggestion">Suggestion</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="issue" id="issue" />
                <Label htmlFor="issue">Issue</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="compliment" id="compliment" />
                <Label htmlFor="compliment">Compliment</Label>
              </div>
            </RadioGroup>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="feedback">Your feedback</Label>
            <Textarea
              id="feedback"
              value={feedbackText}
              onChange={(e) => setFeedbackText(e.target.value)}
              placeholder="Tell us what you think..."
              className="min-h-[150px]"
              required
            />
          </div>
          
          <DialogFooter className="mt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={loading || !feedbackText.trim()}>
              {loading ? "Submitting..." : "Submit Feedback"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default FeedbackDialog;
